import { Stack } from 'component-library';
import { useTranslation } from 'react-i18next';

import { ConservationMethodology } from '@/api/rest/resources/conservation';
import { getDisplayNumber } from '@/hooks/useDisplayNumber';
import { useProject } from '@/pages/shared/projects/pages/project/pages/conservation/shared/hooks/useProject';

export const LegendScale = ({ customLegendRange }: { customLegendRange?: number[] }) => {
  const project = useProject();
  const isBUProject = project?.natureUnitVariant === ConservationMethodology.bu;

  const integerFormatter = (number: number) =>
    getDisplayNumber(number, window.navigator.language, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  const floatFormatter = (number: number) =>
    getDisplayNumber(number, window.navigator.language, {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    });
  if (customLegendRange && customLegendRange.length > 0) {
    const formatter = customLegendRange.some((num) => !Number.isInteger(num)) ? floatFormatter : integerFormatter;

    return <LegendScaleContainer range={customLegendRange} formatter={formatter} />;
  }

  if (!isBUProject) {
    return <LegendScaleContainer range={[0.0, 0.2, 0.4, 0.6, 0.8, 1.0]} formatter={floatFormatter} />;
  }

  return <LegendScaleContainer range={[0, 20, 40, 60, 80, 100]} formatter={integerFormatter} />;
};

const LegendScaleContainer = ({ range, formatter }: { range: number[]; formatter: (num: number) => string }) => {
  return (
    <Stack direction='row' className='justify-between w-full' data-testid='legend-scale'>
      {range.map((num) => (
        <span key={num} className='typography-caption text-text-secondary'>
          {formatter(num)}
        </span>
      ))}
    </Stack>
  );
};

export const SpeciesOccupancyLegend = () => {
  const { t } = useTranslation();
  const project = useProject();

  const numberOfGroups = Object.keys(project?.speciesGroups || {}).length;
  return (
    <Stack direction='col' spacing={2}>
      <LegendCircles steps={numberOfGroups + 1} />
      <span className='typography-caption text-text-secondary'>
        {t('shared.ncaDetail.details.landMonitoring.floraAndFauna.speciesOccupancyLegend')}
      </span>
    </Stack>
  );
};

const LegendCircles = ({ steps }: { steps: number }) => {
  const start = { r: 232, g: 231, b: 243 }; // #E8E7F3
  const end = { r: 89, g: 90, b: 144 }; // #595A90

  const colors = Array.from({ length: steps }, (_, i) => {
    const t = i / (steps - 1);
    const r = Math.round(start.r + (end.r - start.r) * t);
    const g = Math.round(start.g + (end.g - start.g) * t);
    const b = Math.round(start.b + (end.b - start.b) * t);
    return `rgb(${r}, ${g}, ${b})`;
  });

  return (
    <Stack spacing={3} direction='row'>
      {colors.map((c, i) => (
        <Stack key={i} direction='col' spacing={1} center>
          <div
            key={i}
            className='rounded-full'
            style={{
              width: '12px',
              height: '12px',
              backgroundColor: c,
            }}
          />
          <span className='typography-caption text-text-secondary'>{i}</span>
        </Stack>
      ))}
    </Stack>
  );
};
